import type { ForYouFeedItem } from "../../forYou/hooks/useGetForYouRecommendedContentQuery";
import { displayToast } from "../../../components/app/AppToast";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import {
  convertArticleToItemData,
  gaEventArticleShare,
} from "../../../tools/analytics/domains/article.analytics";
import { gaEventContentShare } from "../../../tools/analytics/domains/content.analytics";
import { useShareRoomId } from "../../room/hooks/useShareRoomId";
import { useOnboardingGuard } from "./useOnboardingGuard";
import { useTranslation } from "react-i18next";
import type { CurrentUser } from "./useCurrentUser";
import { apiClient, useApiRevalidate } from "../../../services/api";
import type { SearchArticleItem } from "../../../features/search/hooks/useSearchArticlesQuery";
import type {
  ArticleDetail,
  RelatedArticleCard,
} from "../../../features/article/hooks/useArticleData";
import { shareUrl } from "../../app/utils/shareUrl";
import { REDIRECT_HOST } from "../../../config";

type FeedArticleItem = Extract<ForYouFeedItem, { content_format: "Article" }>;

type ShareableArticle =
  ArticleDetail | RelatedArticleCard | SearchArticleItem | FeedArticleItem;

export type UseShareArticle = {
  triggerShare: (article: ShareableArticle, user: CurrentUser) => Promise<void>;
};

export function useShareArticle(
  analyticsListName: AnalyticsItemList
): UseShareArticle {
  const revalidate = useApiRevalidate();
  const { t } = useTranslation();
  const onboardingGuard = useOnboardingGuard();
  const shareRoomId = useShareRoomId();

  const triggerShare = async (
    article: ShareableArticle,
    currentUser: CurrentUser
  ): Promise<void> => {
    try {
      if (
        !onboardingGuard(
          t("🔒 Please complete your profile first to share this content. ")
        )
      ) {
        return;
      }

      // The fallback when the short link cannot be minted. It keeps the room
      // too, or the fallback quietly undoes the fix.
      const path =
        shareRoomId === null
          ? `/post/${article.slug}`
          : `/room/post/${shareRoomId}/${article.slug}`;
      const longUrl = `${globalThis.location.origin}${path}?shared=${currentUser.id}`;
      // Not awaited: `shareUrl` must start inside the click (see there).
      const link = apiClient
        .post("/webapp/private/articles/:id/shares", {
          param: { id: article.id },
        })
        .then(() => {
          void revalidate("/webapp/public/articles/:slug", {
            param: { slug: article.slug },
          });
          return apiClient
            .post("/webapp/private/share-links", {
              json: {
                type: "article",
                slug: article.slug,
                roomId: shareRoomId,
              },
            })
            .then(({ body }) => `https://${REDIRECT_HOST}/${body}`)
            .catch(() => longUrl);
        });
      const text = t(
        "I think you might be interested in this scientific publication: {{title}}.",
        { title: article.title }
      );

      await shareUrl(article.title, text, link);

      gaEventArticleShare({
        article,
        listName: analyticsListName,
      });
      gaEventContentShare(
        convertArticleToItemData({
          article,
          listName: analyticsListName,
        })
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  };

  return { triggerShare };
}
